import { baseApi, Tag } from './base'
import { cacher } from '@/utils/rtkQueryCacheUtils'
import { getSocket } from '@/core/api/socket'
import { SocketEvent } from '@/core/types/socket-event'

// ─── Types ───────────────────────────────────────────────────

export type TimeTrackingConsent = {
  enabled: boolean
  firstTime: boolean
}

export type TimeSessionStaff = {
  _id: string
  status?: string
  active?: boolean
  userInfo?: {
    name?: string
    surname?: string
  }
}

export type TimeSession = {
  _id: string
  staff: TimeSessionStaff
  establishment?: string
  startTime: string
  endTime: string | null
  duration: number | null
  closureType: 'none' | 'manual' | 'automatic' | 'browser_close' | 'forced_second_device' | 'admin_close'
  source: 'system' | 'correction_doctor' | 'correction_admin'
  isOpen: boolean
  deviceId: string | null
  wasModified?: boolean
  createdAt: string
  updatedAt: string
}

export type WeeklyTotal = {
  _id: { year: number; week: number }
  totalSeconds: number
  sessionCount: number
}

export type GetSessionsResponse = {
  items: TimeSession[]
  totalCount: number
  totals: WeeklyTotal[]
  openSessionCount: number
}

export type StartSessionResponse =
  | { conflict: false; session: TimeSession }
  | { conflict: true; existingSession: TimeSession }

export type TimeNotification = {
  _id: string
  staff: string
  session: string
  message: string
  read: boolean
  createdAt: string
}

export type AuditChange = {
  field: string
  oldValue: unknown
  newValue: unknown
}

export type TimeAuditLog = {
  _id: string
  session: string | null
  staff: string
  action: 'session_created' | 'session_closed' | 'session_corrected' | 'session_created_admin' | 'consent_enabled' | 'consent_disabled'
  performedBy: string
  performedByRole: 'doctor' | 'admin_facility' | 'admin_network'
  reason: string | null
  comment: string | null
  changes: AuditChange[]
  createdAt: string
}

export type SessionQueryParams = {
  from: string
  to: string
  staff?: string
  network?: string
  skip?: number
  limit?: number
}

export type TimeTrackingAdminStaff = {
  _id: string
  email?: string
  status?: string
  userInfo: {
    name: string
    surname: string
  }
  establishment?: {
    _id: string
    name?: string
  }
}

export type AdminCreateSessionParams = {
  staff: string
  startTime: string
  endTime: string
  establishment?: string
  reasonCode: 'forgotten_clock' | 'technical_issue' | 'recovered_leave' | 'data_entry_error'
  comment?: string
}

export type CorrectSessionParams = {
  sessionId: string
  startTime?: string
  endTime?: string
}

export type AdminCorrectSessionParams = CorrectSessionParams & {
  reasonCode: 'forgotten_clock' | 'technical_issue' | 'recovered_leave' | 'data_entry_error'
  comment?: string
}

// ─── API ─────────────────────────────────────────────────────

export const timeTrackingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Consent
    getTimeTrackingConsent: builder.query<TimeTrackingConsent, void>({
      query: () => '/time-tracking/consent',
      providesTags: [Tag.TIME_TRACKING_CONSENT],
    }),
    updateTimeTrackingConsent: builder.mutation<TimeTrackingConsent, { enabled: boolean }>({
      query: (body) => ({ url: '/time-tracking/consent', method: 'PUT', body }),
      invalidatesTags: [Tag.TIME_TRACKING_CONSENT],
    }),

    // Session start/stop
    startTimeTrackingSession: builder.mutation<StartSessionResponse, { deviceId: string; establishment?: string }>({
      query: (body) => ({ url: '/time-tracking/sessions/start', method: 'POST', body }),
      invalidatesTags: [Tag.TIME_TRACKING_SESSION],
    }),
    forceStartTimeTrackingSession: builder.mutation<TimeSession, { deviceId: string; establishment?: string }>({
      query: (body) => ({ url: '/time-tracking/sessions/force-start', method: 'POST', body }),
      invalidatesTags: [Tag.TIME_TRACKING_SESSION],
    }),
    stopTimeTrackingSession: builder.mutation<TimeSession, void>({
      query: () => ({ url: '/time-tracking/sessions/stop', method: 'POST' }),
      invalidatesTags: [Tag.TIME_TRACKING_SESSION],
    }),
    stopTimeTrackingBeacon: builder.mutation<void, void>({
      query: () => ({ url: '/time-tracking/sessions/stop-beacon', method: 'POST' }),
    }),

    // Sessions list
    getTimeTrackingSessions: builder.query<GetSessionsResponse, SessionQueryParams>({
      query: (params) => ({ url: '/time-tracking/sessions', params }),
      providesTags: [Tag.TIME_TRACKING_SESSION],
    }),

    // Session creation (admin)
    createTimeTrackingSession: builder.mutation<TimeSession, AdminCreateSessionParams>({
      query: (body) => ({ url: '/time-tracking/sessions', method: 'POST', body }),
      invalidatesTags: [Tag.TIME_TRACKING_SESSION],
    }),

    // Session correction
    correctTimeTrackingSession: builder.mutation<TimeSession, CorrectSessionParams | AdminCorrectSessionParams>({
      query: ({ sessionId, ...body }) => ({ url: `/time-tracking/sessions/${sessionId}`, method: 'PATCH', body }),
      invalidatesTags: [Tag.TIME_TRACKING_SESSION],
    }),

    // Notifications
    getTimeTrackingNotifications: builder.query<TimeNotification[], void>({
      query: () => '/time-tracking/notifications',
      providesTags: [Tag.TIME_TRACKING_NOTIFICATION],
    }),
    markTimeTrackingNotificationRead: builder.mutation<TimeNotification, string>({
      query: (notificationId) => ({ url: `/time-tracking/notifications/${notificationId}/read`, method: 'PATCH' }),
      invalidatesTags: [Tag.TIME_TRACKING_NOTIFICATION],
    }),

    // Audit logs (admin)
    getTimeTrackingAuditLogs: builder.query<TimeAuditLog[], { sessionId?: string; staff?: string; limit?: number }>({
      query: (params) => ({ url: '/time-tracking/audit', params }),
    }),

    // Current open session (for session clock)
    getCurrentTimeTrackingSession: builder.query<TimeSession | null, void>({
      query: () => '/time-tracking/sessions/current',
      transformResponse: (response: { session: TimeSession | null }) => response.session,
      providesTags: [Tag.TIME_TRACKING_SESSION],
      async onCacheEntryAdded(_arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        const socket = await getSocket()
        const listener = (session: TimeSession | null) => {
          updateCachedData(() => session)
          dispatch(baseApi.util.invalidateTags([Tag.TIME_TRACKING_SESSION]))
        }

        try {
          await cacheDataLoaded
          socket.on(SocketEvent.TIME_TRACKING_SESSION_UPDATED, listener)
        } catch {}

        await cacheEntryRemoved
        socket.off(SocketEvent.TIME_TRACKING_SESSION_UPDATED, listener)
      },
    }),

    // Admin scope: staffs the current admin can manage (optionally scoped to a single network)
    getTimeTrackingAdminStaffs: builder.query<TimeTrackingAdminStaff[], { network?: string } | void>({
      query: (params) => ({ url: '/time-tracking/admin-staffs', params: params ?? undefined }),
    }),
  }),
  overrideExisting: false,
})
